import { Box, Stack, SxProps, Theme } from "@mui/material";
import { ReactElement } from "react";
import { TILE_GRID_MIN_COLUMN_WIDTH } from "../../../utils/constants";

// `theme.breakpoints.values.sm`, as a container-query threshold.
const SR_TILE_GRID_CONTAINER_BREAKPOINT_SM = 600;
const SR_TILE_GRID_CONTAINER_NAME = "sr-tile-grid";

export type SrTileGridProps = {
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
};

/*
 * Column widths follow the grid's own width (e.g. beside a sidebar), not the
 * viewport. A container query never applies to the element that establishes the
 * container, so the grid is a child of the query host.
 */
export const SrTileGrid = ({ children, sx }: SrTileGridProps): ReactElement => {
  return (
    <Box
      sx={[
        {
          containerType: "inline-size",
          containerName: SR_TILE_GRID_CONTAINER_NAME,
          width: "100%",
        },
        // On the query host rather than the inner grid: a container query
        // measures the host's content box, so a caller's padding here moves
        // the breakpoint with the space the tracks actually get.
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Stack
        sx={{
          display: "grid",
          // `auto-fill`, not `auto-fit`: auto-fit collapses empty tracks and
          // stretches a short row's tiles across the whole row.
          gridTemplateColumns: `repeat(auto-fill, minmax(${TILE_GRID_MIN_COLUMN_WIDTH.xs}px, 1fr))`,
          [`@container ${SR_TILE_GRID_CONTAINER_NAME} (min-width: ${SR_TILE_GRID_CONTAINER_BREAKPOINT_SM}px)`]:
            {
              gridTemplateColumns: `repeat(auto-fill, minmax(${TILE_GRID_MIN_COLUMN_WIDTH.sm}px, 1fr))`,
            },
          gap: 2,
          width: "100%",
        }}
      >
        {children}
      </Stack>
    </Box>
  );
};
